import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { FieldType, MultipleChoiceField, SingleChoiceField } from '@app/api';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  questionLabel: {
    ...theme.fonts.label,
  },
  minMaxLabel: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase,
  },
  required: {
    color: theme.colors.red,
  },
}));

interface Props {
  field: MultipleChoiceField | SingleChoiceField;
}

const pickMinMaxLabel = (field: MultipleChoiceField, t: TFunction) => {
  if (!field.properties.choices) {
    return '';
  }
  const { minSelection, maxSelection } = field.validation || {};
  if (!minSelection && !maxSelection && maxSelection !== 0) {
    return '';
  }

  if (minSelection === maxSelection) {
    return t('screeningForm.sameOption', {
      same: maxSelection,
    });
  }

  if (!minSelection) {
    return t('screeningForm.maxOption', {
      max: maxSelection,
    });
  }

  if (!maxSelection) {
    return t('screeningForm.minOption', {
      min: minSelection,
    });
  }

  return t('screeningForm.minMaxOption', {
    min: minSelection,
    max: maxSelection,
  });
};

export const ChoicesLabel = ({ field }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const multipleChoices = field.type === FieldType.MULTIPLE_CHOICE;

  return (
    <>
      <div>
        <label className={classes.questionLabel}>
          {field.title || t('screeningForm.noQuestion')}{' '}
          {field.validation && field.validation.required && (
            <strong className={classes.required}>*</strong>
          )}
        </label>
      </div>
      {field?.subTitle && (
        <div>
          <label className={classes.minMaxLabel}>{field.subTitle}</label>
        </div>
      )}
      {multipleChoices && (
        <div>
          <label className={classes.minMaxLabel}>
            {pickMinMaxLabel(field, t)}
          </label>
        </div>
      )}
    </>
  );
};
